import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { SmallInputLabel, UserAutocomplete } from "../../../../components";
import {
  OneOnOneMeetingDto,
  useDeleteMeeting,
  useEditOneOnOneMeeting,
  useGetOneOnOneMeetingDetail,
  UserDto,
} from "../../../../http";
import { useDestructiveConfirm } from "../../../../utilities";

interface IProps {
  visible: boolean;
  meeting: OneOnOneMeetingDto;
  onSaved: () => void;
  onCancelled: () => void;
}

interface IEditOneOnOneFormState {
  leadUser: UserDto;
  externalCallUrl: string;
}

const OneOnOneSettingsModal = (props: IProps) => {
  const { isLoading, data: meeting } = useGetOneOnOneMeetingDetail(props.meeting.guid, props.visible);
  const { mutate: editMeeting } = useEditOneOnOneMeeting();
  const { mutate: deleteMeeting, isLoading: isDeletingMeeting } = useDeleteMeeting();
  const confirm = useDestructiveConfirm();
  const { enqueueSnackbar } = useSnackbar();

  const { control, handleSubmit, reset } = useForm<IEditOneOnOneFormState>({
    defaultValues: { leadUser: props.meeting.leadUser, externalCallUrl: "" },
  });

  useEffect(() => {
    if (!isLoading && meeting != null) {
      reset({
        leadUser: meeting.leadUser,
        externalCallUrl: meeting.externalCallUrl ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, meeting]);

  async function handleDeleteClicked() {
    if (meeting == null) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Delete Meeting",
      content:
        "Your private Meeting Notes and the record of occurrence of the meetings will be deleted. Are you sure you want to delete this meeting?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteMeeting(
      {
        meetingGuid: props.meeting.guid,
      },
      {
        onSuccess: () => {
          enqueueSnackbar(
            `1:1 meeting for ${meeting.leadUser.displayName} and ${meeting.otherUser.displayName} has been deleted.`
          );
          props.onSaved();
        },
      }
    );
  }
  async function handleSaveClicked() {
    if (meeting == null) {
      return;
    }

    handleSubmit((formState) => {
      editMeeting(
        {
          meetingGuid: props.meeting.guid,
          newLeadUserId: formState.leadUser.userId,
          externalCallUrl: formState.externalCallUrl,
        },
        {
          onSuccess: () => {
            reset(undefined, { keepDefaultValues: true });
            props.onSaved();
          },
        }
      );
    })();
  }

  function handleClose(_event: object, reason: "backdropClick" | "escapeKeyDown") {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    handleCancelClicked();
  }

  function handleCancelClicked() {
    reset(undefined, { keepDefaultValues: true });
    props.onCancelled();
  }

  const isEditable = !isDeletingMeeting && meeting?.currentInstance == null;

  return (
    <Dialog open={props.visible} onClose={handleClose}>
      <DialogTitle>1:1 Meeting Settings</DialogTitle>
      <DialogContent>
        {isLoading || meeting == null ? (
          <Stack spacing={1}>
            {[...Array(3)].map((_, i) => (
              <Skeleton variant="rectangular" key={i} height={43} />
            ))}
          </Stack>
        ) : (
          <Stack spacing={0.5}>
            <Stack spacing={0.25}>
              <SmallInputLabel>Lead</SmallInputLabel>
              <Controller
                name="leadUser"
                control={control}
                rules={{ required: "Lead is required" }}
                render={({ field }) => (
                  <UserAutocomplete
                    optionsFilter={(u) => u.userId !== props.meeting.otherUser.userId}
                    disableClearable
                    disabled={!isEditable && !props.visible}
                    onChange={(value) => field.onChange(value)}
                    value={field.value}
                  />
                )}
              />
            </Stack>

            <Stack spacing={0.25}>
              <SmallInputLabel>Team Member</SmallInputLabel>
              <UserAutocomplete placeholder={"Select a person"} value={meeting!.otherUser} disableClearable disabled />
            </Stack>

            <Stack spacing={0.25}>
              <SmallInputLabel>{"Video Call Link (Optional)"}</SmallInputLabel>
              <Controller
                name="externalCallUrl"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="Enter link to your video call (Teams, Zoom, etc.)"
                    disabled={!isEditable}
                  />
                )}
              />
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <Stack direction="row" sx={{ px: 1, pb: 1, justifyContent: "space-between" }}>
        <Button
          variant="contained"
          color="error"
          endIcon={isDeletingMeeting ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null}
          disabled={!isEditable}
          onClick={handleDeleteClicked}
        >
          Delete Meeting
        </Button>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" disabled={isLoading} onClick={handleCancelClicked}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isLoading}
            endIcon={isLoading ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null}
            onClick={handleSaveClicked}
            sx={{ ml: 1 }}
          >
            Save
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export { OneOnOneSettingsModal };
